import React, { useState, useEffect } from 'react';
import { toast } from 'react-toastify';
import { MdVideoLibrary, MdRefresh, MdOpenInNew, MdVisibility, MdThumbUp, MdComment } from 'react-icons/md';
import { FaYoutube, FaFacebook, FaTiktok, FaInstagram } from 'react-icons/fa';
import { getChannels } from '../utils/api';

const platformIcons = {
  youtube: <FaYoutube style={{ color: '#ff0000', fontSize: '18px' }} />,
  facebook: <FaFacebook style={{ color: '#1877f2', fontSize: '18px' }} />,
  tiktok: <FaTiktok style={{ color: '#e0e0e0', fontSize: '18px' }} />,
  instagram: <FaInstagram style={{ color: '#e1306c', fontSize: '18px' }} />
};

const ChannelVideos = () => {
  const [channels, setChannels] = useState([]);
  const [selectedChannel, setSelectedChannel] = useState(null);
  const [channelVideos, setChannelVideos] = useState({});
  const [loading, setLoading] = useState(false);
  const [loadingChannels, setLoadingChannels] = useState(true);

  useEffect(() => { fetchChannels(); }, []);

  const fetchChannels = async () => {
    try {
      const res = await getChannels();
      const allChannels = res.data.data || [];
      setChannels(allChannels);
      if (allChannels.length > 0) setSelectedChannel(allChannels[0]);
    } catch (error) {
      toast.error('Failed to load channels');
    } finally {
      setLoadingChannels(false);
    }
  };

  const fetchChannelVideos = async (channel) => {
    if (channelVideos[channel.id]) {
      setSelectedChannel(channel);
      return;
    }
    setSelectedChannel(channel);
    setLoading(true);
    try {
      const response = await fetch(`https://vantage-production-4475.up.railway.app/api/channel-videos/${channel.id}`);
      const data = await response.json();
      if (data.success) {
        setChannelVideos(prev => ({
          ...prev,
          [channel.id]: data.data.videos || []
        }));
        toast.success(`Loaded ${data.data.total} videos from ${channel.name}`);
      } else {
        toast.error(data.message || 'Failed to load videos');
      }
    } catch (error) {
      toast.error('Failed to fetch: ' + error.message);
    } finally {
      setLoading(false);
    }
  };

  const refreshChannel = async (channel) => {
    setChannelVideos(prev => {
      const updated = { ...prev };
      delete updated[channel.id];
      return updated;
    });
    await fetchChannelVideos(channel);
  };

  const openObsidianEye = (video) => {
    localStorage.setItem('obsidianeye_video', JSON.stringify(video));
    toast.success('Video loaded in ObsidianEye!');
  };

  const openGuardianAI = (video) => {
    localStorage.setItem('guardian_video', JSON.stringify(video));
    toast.success('Video loaded in GuardianAI!');
  };

  const currentVideos = selectedChannel ? (channelVideos[selectedChannel.id] || []) : [];
  const isLoaded = selectedChannel ? !!channelVideos[selectedChannel.id] : false;

  return (
    <div className="page-container">
      <div className="page-header">
        <h1><span className="gradient-text">Channel Videos</span></h1>
        <p>View and analyse videos from all your connected channels</p>
      </div>

      <div className="grid-2" style={{ gap: '20px' }}>

        {/* Left — Channel List */}
        <div>
          <div className="card">
            <div className="card-header">
              <span className="card-title">Your Channels</span>
              <span className="badge badge-info">{channels.length} total</span>
            </div>

            {loadingChannels ? (
              <div className="loading"><div className="spinner"></div></div>
            ) : channels.length === 0 ? (
              <div className="empty-state">
                <MdVideoLibrary style={{ fontSize: '48px', color: 'var(--primary)', opacity: 0.3, marginBottom: '12px' }} />
                <h3>No channels found</h3>
                <p>Add channels in Platform Manager first</p>
              </div>
            ) : (
              <div style={{ display: 'flex', flexDirection: 'column', gap: '10px' }}>
                {channels.map(channel => (
                  <div key={channel.id} style={{
                    padding: '14px 16px', background: 'var(--bg-dark)',
                    borderRadius: '12px', cursor: 'pointer',
                    border: `2px solid ${selectedChannel?.id === channel.id ? 'var(--primary)' : 'var(--border)'}`,
                    transition: 'all 0.2s ease'
                  }}
                    onMouseEnter={e => {
                      if (selectedChannel?.id !== channel.id)
                        e.currentTarget.style.borderColor = 'rgba(0,200,83,0.3)';
                    }}
                    onMouseLeave={e => {
                      if (selectedChannel?.id !== channel.id)
                        e.currentTarget.style.borderColor = 'var(--border)';
                    }}
                    onClick={() => setSelectedChannel(channel)}
                  >
                    <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
                      <div style={{ display: 'flex', alignItems: 'center', gap: '10px' }}>
                        {platformIcons[channel.platform]}
                        <div>
                          <div style={{ fontWeight: '600', fontSize: '14px', color: 'var(--text-primary)' }}>
                            {channel.name}
                          </div>
                          <div style={{ fontSize: '11px', color: 'var(--text-secondary)', textTransform: 'capitalize' }}>
                            {channel.platform}
                            {channel.has_oauth && (
                              <span style={{ color: 'var(--primary)', marginLeft: '6px' }}>● Connected</span>
                            )}
                          </div>
                        </div>
                      </div>
                      <div style={{ display: 'flex', gap: '6px' }}>
                        {channelVideos[channel.id] && (
                          <span className="badge badge-success" style={{ fontSize: '10px' }}>
                            {channelVideos[channel.id].length} loaded
                          </span>
                        )}
                      </div>
                    </div>
                  </div>
                ))}
              </div>
            )}
          </div>
        </div>

        {/* Right — Videos */}
        <div>
          {selectedChannel && (
            <div className="card">
              <div className="card-header">
                <span className="card-title" style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                  {platformIcons[selectedChannel.platform]}
                  {selectedChannel.name}
                </span>
                <div style={{ display: 'flex', gap: '8px' }}>
                  {isLoaded && (
                    <button className="btn btn-secondary btn-sm"
                      onClick={() => refreshChannel(selectedChannel)}>
                      <MdRefresh /> Refresh
                    </button>
                  )}
                  {!isLoaded && (
                    <button className="btn btn-primary btn-sm"
                      onClick={() => fetchChannelVideos(selectedChannel)}
                      disabled={loading}>
                      {loading
                        ? <><div className="spinner" style={{ width: '14px', height: '14px', borderWidth: '2px' }} /> Loading...</>
                        : <><MdRefresh /> Load Videos</>}
                    </button>
                  )}
                </div>
              </div>

              {!isLoaded && !loading && (
                <div style={{ textAlign: 'center', padding: '60px 20px' }}>
                  <MdVideoLibrary style={{ fontSize: '56px', color: 'var(--primary)', opacity: 0.2, marginBottom: '16px' }} />
                  <h3 style={{ color: 'var(--text-primary)', marginBottom: '8px' }}>
                    Ready to load {selectedChannel.name}
                  </h3>
                  <p style={{ color: 'var(--text-secondary)', fontSize: '14px', marginBottom: '20px' }}>
                    Click Load Videos to import existing videos from this channel
                  </p>
                  <button className="btn btn-primary"
                    onClick={() => fetchChannelVideos(selectedChannel)}>
                    <MdRefresh /> Load Videos
                  </button>
                </div>
              )}

              {loading && (
                <div style={{ textAlign: 'center', padding: '60px' }}>
                  <div className="spinner" style={{ margin: '0 auto 16px', width: '32px', height: '32px', borderWidth: '3px' }} />
                  <p style={{ color: 'var(--text-secondary)' }}>Loading videos...</p>
                </div>
              )}

              {isLoaded && currentVideos.length === 0 && (
                <div className="empty-state">
                  <p>No videos found for this channel</p>
                </div>
              )}

              {isLoaded && currentVideos.length > 0 && (
                <>
                  <div style={{
                    background: 'rgba(0,200,83,0.08)', border: '1px solid rgba(0,200,83,0.2)',
                    borderRadius: '10px', padding: '10px 14px', marginBottom: '16px',
                    fontSize: '12px', color: 'var(--text-secondary)'
                  }}>
                    Nothing runs automatically. Click ObsidianEye or GuardianAI on any video to analyse it.
                  </div>

                  <div style={{ display: 'flex', flexDirection: 'column', gap: '12px' }}>
                    {currentVideos.map((video, index) => (
                      <div key={video.id} style={{
                        background: 'var(--bg-dark)', borderRadius: '12px',
                        border: '1px solid var(--border)', overflow: 'hidden',
                        transition: 'all 0.2s ease'
                      }}
                        onMouseEnter={e => e.currentTarget.style.borderColor = 'rgba(0,200,83,0.3)'}
                        onMouseLeave={e => e.currentTarget.style.borderColor = 'var(--border)'}
                      >
                        <div style={{ display: 'flex', gap: '14px', padding: '14px' }}>
                          {/* Thumbnail */}
                          <div style={{
                            width: '120px', height: '68px', borderRadius: '8px',
                            background: 'var(--bg-card)', flexShrink: 0, overflow: 'hidden',
                            position: 'relative'
                          }}>
                            {video.thumbnail ? (
                              <img src={video.thumbnail} alt="thumbnail"
                                style={{ width: '100%', height: '100%', objectFit: 'cover' }}
                                onError={e => { e.target.style.display = 'none'; }} />
                            ) : (
                              <div style={{
                                width: '100%', height: '100%',
                                display: 'flex', alignItems: 'center', justifyContent: 'center'
                              }}>
                                <MdVideoLibrary style={{ fontSize: '24px', color: 'var(--border)' }} />
                              </div>
                            )}
                            <div style={{
                              position: 'absolute', top: '4px', left: '4px',
                              background: 'rgba(0,0,0,0.8)', borderRadius: '4px',
                              padding: '2px 5px', fontSize: '10px', fontWeight: '700', color: 'white'
                            }}>
                              #{index + 1}
                            </div>
                          </div>

                          {/* Info */}
                          <div style={{ flex: 1, minWidth: 0 }}>
                            <div style={{
                              fontWeight: '600', fontSize: '13px', color: 'var(--text-primary)',
                              marginBottom: '4px', overflow: 'hidden',
                              textOverflow: 'ellipsis', whiteSpace: 'nowrap'
                            }}>
                              {video.title}
                            </div>
                            <div style={{ fontSize: '11px', color: 'var(--text-secondary)', marginBottom: '8px' }}>
                              {new Date(video.published_at).toLocaleDateString()}
                            </div>

                            {/* Stats */}
                            <div style={{ display: 'flex', gap: '12px' }}>
                              {video.views !== undefined ? (
                                <>
                                  <div style={{ display: 'flex', alignItems: 'center', gap: '4px', fontSize: '12px', color: 'var(--text-secondary)' }}>
                                    <MdVisibility style={{ fontSize: '14px' }} />
                                    {Number(video.views).toLocaleString()}
                                  </div>
                                  <div style={{ display: 'flex', alignItems: 'center', gap: '4px', fontSize: '12px', color: 'var(--text-secondary)' }}>
                                    <MdThumbUp style={{ fontSize: '14px' }} />
                                    {Number(video.likes).toLocaleString()}
                                  </div>
                                  <div style={{ display: 'flex', alignItems: 'center', gap: '4px', fontSize: '12px', color: 'var(--text-secondary)' }}>
                                    <MdComment style={{ fontSize: '14px' }} />
                                    {Number(video.comments).toLocaleString()}
                                  </div>
                                </>
                              ) : (
                                <div style={{
                                  display: 'flex', alignItems: 'center', gap: '4px',
                                  fontSize: '11px', color: 'var(--text-light)',
                                  background: 'var(--bg-card)', padding: '3px 8px',
                                  borderRadius: '6px', border: '1px solid var(--border)'
                                }}>
                                  📊 Stats available after Analytics API setup
                                </div>
                              )}
                            </div>
                          </div>

                          {/* Actions */}
                          <div style={{ display: 'flex', flexDirection: 'column', gap: '6px', flexShrink: 0 }}>
                            <button
                              className="btn btn-primary btn-sm"
                              onClick={() => openObsidianEye(video)}
                              style={{ whiteSpace: 'nowrap', fontSize: '12px' }}
                            >
                              👁 ObsidianEye
                            </button>
                            <button
                              className="btn btn-secondary btn-sm"
                              onClick={() => openGuardianAI(video)}
                              style={{
                                whiteSpace: 'nowrap', fontSize: '12px',
                                borderColor: 'rgba(0,200,83,0.3)', color: 'var(--primary)'
                              }}
                            >
                              🛡 GuardianAI
                            </button>
                            <button
                              className="btn btn-secondary btn-sm"
                              onClick={() => window.open(video.url, '_blank')}
                              style={{ whiteSpace: 'nowrap', fontSize: '12px' }}
                            >
                              <MdOpenInNew /> View
                            </button>
                          </div>
                        </div>
                      </div>
                    ))}
                  </div>
                </>
              )}
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

export default ChannelVideos;